import React from "react";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { COLLECTION_HEADLINE_MACROS, COLLECTION_DESCRIPTION_MACROS } from "@/types/bulkAd";
import type { CollectionData } from "@/types/bulkAd";
import { ImagePlus } from "lucide-react";

interface CollectionPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  value: CollectionData | null;
  onChange: (data: CollectionData) => void;
  onSelectCreative: () => void;
}

const DESCRIPTION_MACROS = COLLECTION_DESCRIPTION_MACROS;

function buildDefaults(value: CollectionData | null): CollectionData {
  return {
    creativeName: value?.creativeName || "",
    catalogId: null,
    catalogName: null,
    productSetId: null,
    productSetName: null,
    headlineType: value?.headlineType || "macro",
    headlineValue: value?.headlineValue || "{{product.name}}",
    buttonLabel: value?.buttonLabel || "",
    destinationUrl: "",
    itemHeadline: value?.itemHeadline || "{{product.name}}",
    itemDescription: value?.itemDescription || "{{product.current_price}}",
    descriptionType: value?.descriptionType || "macro",
  };
}

export function CollectionPopover({
  isOpen,
  onClose,
  value,
  onChange,
  onSelectCreative,
}: CollectionPopoverProps) {
  // Local editing state — only committed on Save
  const [local, setLocal] = useState<CollectionData>(() => buildDefaults(value));

  // Reset local state when popup opens with new value
  useEffect(() => {
    if (isOpen) {
      setLocal(buildDefaults(value));
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (partial: Partial<CollectionData>) => {
    setLocal((prev) => ({ ...prev, ...partial }));
  };

  /** Called externally after creative selector picks an asset */
  // The parent (AdSheetTable) updates value.creativeName after selection,
  // so we sync it into local state when it changes while open
  useEffect(() => {
    if (isOpen && value?.creativeName && value.creativeName !== local.creativeName) {
      setLocal((prev) => ({ ...prev, creativeName: value.creativeName }));
    }
  }, [value?.creativeName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    onChange(local);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">Instant Experience Storefront</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Cover Creative */}
          <div className="space-y-1">
            <Label className="text-xs">Cover Creative</Label>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start text-xs h-8"
              onClick={onSelectCreative}
            >
              {local.creativeName ? (
                <span className="truncate">{local.creativeName}</span>
              ) : (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <ImagePlus className="h-3 w-3" /> Select cover...
                </span>
              )}
            </Button>
          </div>

          {/* Label */}
          <div className="space-y-1">
            <Label className="text-xs">Label</Label>
            <Input
              value={local.buttonLabel}
              onChange={(e) => update({ buttonLabel: e.target.value })}
              placeholder="Enter label"
              className="h-8 text-xs"
            />
          </div>

          {/* Product Headline */}
          <div className="space-y-1">
            <Label className="text-xs">Product Headline</Label>
            <Select
              value={local.itemHeadline}
              onValueChange={(val) =>
                update({ itemHeadline: val, headlineValue: val, headlineType: "macro" })
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select macro..." />
              </SelectTrigger>
              <SelectContent>
                {COLLECTION_HEADLINE_MACROS.map((m) => (
                  <SelectItem key={m.value} value={m.value} className="text-xs">
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Product Description */}
          <div className="space-y-1">
            <Label className="text-xs">Product Description</Label>
            <Select
              value={local.itemDescription}
              onValueChange={(val) => update({ itemDescription: val, descriptionType: "macro" })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select macro..." />
              </SelectTrigger>
              <SelectContent>
                {DESCRIPTION_MACROS.map((m) => (
                  <SelectItem key={m.value} value={m.value} className="text-xs">
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
